import { inject } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { FamilySettingsConstants as ui } from '../../Config/UIConstants';
import { IUserFromServer } from '../../Services/UserService';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { RemovalConfirmationPopup } from '../../SharedComponents/RemovalConfirmationPopup';
import { SuggestionTextInput } from '../../SharedComponents/SuggestionTextInput';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { UserStore } from '../../_stores/UserStore';
import { largeShadow } from '../../styles/styleObjects';
import { validateEmail } from '../../util/validateEmail';

interface IProps {
    store: ApplicationStore;
}

interface IState {
    email: string;
    firstName: string;
    showConfirmation: boolean;
    showExitConfirm: boolean;
    inviteFailed: string | undefined;
}

@inject('store')
export class AddFamilyMemberPage extends React.Component<RouteComponentProps<any> & IProps, IState> {
    public state: IState = {
        email: '',
        firstName: '',
        showConfirmation: false,
        showExitConfirm: false,
        inviteFailed: undefined,
    };

    private onPressAddMember = (): void => {
        this.setState({ showConfirmation: true });
        const alreadyInvited = this.props.store.userFamilyStore.getFamilyByID(parseInt(this.props.match.params.id))
            .pendingMembers;
        const emailsAlreadyInFamily = this.props.store.userFamilyStore
            .getFamilyByID(parseInt(this.props.match.params.id))
            .members.map(user => {
                const member = this.props.store.userFamilyStore.getFamilyMemberByID(user.id);
                if (!member) {
                    throw new Error('Failed to load member: ' + user.id);
                }
                return member.email;
            });

        if (alreadyInvited.some(user => user.email === this.state.email)) {
            this.setState({ inviteFailed: ui.INVITE_FAILED_INVITE(this.state.email) });
            return;
        } else if (emailsAlreadyInFamily.some(email => email === this.state.email)) {
            this.setState({ inviteFailed: ui.INVITE_FAILED_FAMILY(this.state.email) });
            return;
        } else {
            this.setState({ inviteFailed: undefined });
        }

        this.props.store.userFamilyStore.inviteUserToFamily(
            parseInt(this.props.match.params.id),
            this.state.email,
            this.state.firstName,
        );
    }

    private onLeavePage = () => {
        if (this.props.location.state && this.props.location.state.cameFromStoryID) {
            this.props.history.push(Routes.PHOTO_VIEWER.replace(':id', this.props.location.state.cameFromStoryID));
        } else {
            this.props.history.push(`/familydetail/${parseInt(this.props.match.params.id)}`);
        }
    }

    private getSuggestionsForKey(
        stateKey: keyof IState,
        userKey: keyof IUserFromServer,
    ): Array<{ display: string; value: IUserFromServer }> {
        const lowercaseStringFromState = (this.state[stateKey] as string).toLocaleLowerCase();

        return this.state[stateKey]!.toString().length > 0
            ? this.props.store.userFamilyStore.users
                  .filter(user => (user[userKey] as string).toLocaleLowerCase().includes(lowercaseStringFromState))
                  .map(user => ({
                      display: `${UserStore.getPublicUserFullName(user)} (${user.email})`,
                      value: user,
                  }))
            : [];
    }

    private get suggestions(): Array<{ display: string; value: IUserFromServer }> {
        return [
            ...this.getSuggestionsForKey('firstName', 'first_name'),
            ...this.getSuggestionsForKey('email', 'email'),
        ];
    }

    private onPressSuggestion = (suggestion: { first_name: string; email: string }) => {
        this.setState({ firstName: suggestion.first_name, email: suggestion.email });
    }

    public render() {
        return (
            <FullScreenModalContainer {...this.props} onPressExit={() => this.setState({ showExitConfirm: true })}>
                <FullContainer style={styles.container}>
                    <p style={styles.headerText}>
                        {ui.ADD_MEMBER(this.props.store.userFamilyStore.getFamilyByID(parseInt(this.props.match.params.id)).name)}
                    </p>
                    <SuggestionTextInput
                        suggestions={this.suggestions}
                        header={ui.FIRST_NAME}
                        onChangeText={text => this.setState({ firstName: text })}
                        value={this.state.firstName}
                        containerStyle={styles.topInput}
                        placeholder={ui.REQUIRED_PLACEHOLDER}
                        onPressSuggestion={this.onPressSuggestion}
                    />
                    <SuggestionTextInput
                        suggestions={this.suggestions}
                        header={ui.EMAIL}
                        onChangeText={text => this.setState({ email: text })}
                        value={this.state.email}
                        containerStyle={styles.textInput}
                        placeholder={ui.REQUIRED_PLACEHOLDER}
                        onPressSuggestion={this.onPressSuggestion}
                    />
                </FullContainer>
                {this.state.showConfirmation && (
                    <ExitableAlert
                        headerText={
                            this.state.inviteFailed !== undefined
                                ? this.state.inviteFailed
                                : `${ui.INVITE_CONFIRM} ${this.state.email}`
                        }
                        confirmText={ui.OK}
                        onConfirmClick={
                            this.state.inviteFailed
                                ? () => this.setState({ showConfirmation: false, inviteFailed: undefined })
                                : this.onLeavePage
                        }
                    />
                )}
                <ModalActionButtonBar
                    confirmOnClick={() => this.onPressAddMember()}
                    confirmContent={ui.ADD}
                    altPadding={25}
                    confirmDisabled={this.state.firstName === '' || !validateEmail(this.state.email)}
                />

                {this.state.showExitConfirm && (
                    <RemovalConfirmationPopup
                        headerText={ui.EXIT_HEADER}
                        subHeaderText={ui.EXIT_SUB_HEADER}
                        cancelText={ui.GO_BACK}
                        removeText={ui.LEAVE_PAGE}
                        onClickCancel={() => this.setState({ showExitConfirm: false })}
                        onClickRemove={() => this.onLeavePage()}
                    />
                )}
            </FullScreenModalContainer>
        );
    }
}

const styles = {
    container: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
    } as React.CSSProperties,
    headerText: {
        fontSize: 25,
        color: '#423F4E',
        fontWeight: 900,
        marginTop: 70,
        height: 50,
    } as React.CSSProperties,
    topInput: {
        width: 400,
        height: 75,
        backgroundColor: 'white',
        marginTop: 40,
        boxShadow: largeShadow,
    },
    textInput: {
        width: 400,
        height: 75,
        backgroundColor: 'white',
        marginTop: 20,
        boxShadow: largeShadow,
    },
};



// WEBPACK FOOTER //
// ./src/Pages/FamiliesPage/AddFamilyMemberPage.tsx